import React, { FC, useMemo } from 'react';
import * as THREE from 'three';
import { extend } from '@react-three/fiber';
import { Instances, Instance, RoundedBox } from '@react-three/drei';
import { RoundedBoxGeometry } from 'three-stdlib';

// Shared materials to prevent re-allocation
const FRAME_MAT = new THREE.MeshStandardMaterial({ color: "#475569", metalness: 0.4, roughness: 0.6, transparent: true });
const CUSHION_MAT = new THREE.MeshPhysicalMaterial({ 
  color: "#334155", 
  roughness: 0.9, 
  sheen: 1, 
  sheenRoughness: 0.5, 
  sheenColor: "#64748b",
  emissive: "#38bdf8",
  emissiveIntensity: 0.05,
  transparent: true 
});
const BACKREST_MAT = new THREE.MeshPhysicalMaterial({ 
  color: "#334155", 
  roughness: 0.9, 
  sheen: 1, 
  sheenColor: "#64748b",
  emissive: "#38bdf8",
  emissiveIntensity: 0.03,
  transparent: true 
});
const ARMREST_MAT = new THREE.MeshPhysicalMaterial({ color: "#475569", metalness: 0.1, roughness: 0.8, transparent: true });
const LEG_MAT = new THREE.MeshStandardMaterial({ color: "#1e293b", metalness: 0.7, roughness: 0.3, transparent: true });
const SHADOW_MAT = new THREE.MeshStandardMaterial({ 
  color: "#38bdf8", 
  transparent: true, 
  opacity: 0.08, 
  depthWrite: false,
  polygonOffset: true,
  polygonOffsetFactor: -1
});

export const Audience: FC = () => {
  // Define geometries once
  const baseGeo = useMemo(() => new RoundedBoxGeometry(3.4, 0.2, 1.3, 4, 0.05), []);
  const cushionGeo = useMemo(() => new RoundedBoxGeometry(1.45, 0.7, 1.1, 8, 0.15), []);
  const backrestGeo = useMemo(() => new RoundedBoxGeometry(1.45, 1.1, 0.4, 8, 0.2), []);
  const armrestGeo = useMemo(() => new RoundedBoxGeometry(0.3, 0.8, 1.25, 8, 0.1), []);
  const legGeo = useMemo(() => new THREE.CylinderGeometry(0.04, 0.02, 0.4, 16), []);
  const shadowGeo = useMemo(() => new THREE.PlaneGeometry(3.8, 2.5), []);

  // Define the grid of sofas once
  const sofas = useMemo(() => {
    const data = [];
    for (let row = 0; row < 4; row++) {
      for (let i = 0; i < 7; i++) {
        data.push({
          position: [(i - 3) * 5.4, 0, 36 + (row * 6)] as [number, number, number],
          rotation: [0, Math.PI, 0] as [number, number, number],
          scale: 1.5,
          opacity: Math.max(0.05, 1 - (row * 0.25))
        });
      }
    }
    return data;
  }, []);

  return (
    <group>
      {/* 1. Base Frames */}
      <Instances range={sofas.length} geometry={baseGeo} material={FRAME_MAT}>
        {sofas.map((s, i) => (
          <Instance key={i} position={s.position} rotation={s.rotation} scale={s.scale} color={new THREE.Color().setScalar(s.opacity)} />
        ))}
      </Instances>

      {/* 2. Main Seat Cushions (Left) */}
      <Instances range={sofas.length} geometry={cushionGeo} material={CUSHION_MAT}>
        {sofas.map((s, i) => (
          <Instance 
            key={i} 
            position={[s.position[0] - 0.75 * s.scale, s.position[1] + 0.55 * s.scale, s.position[2]]} 
            rotation={s.rotation} 
            scale={s.scale} 
          />
        ))}
      </Instances>

      {/* 3. Main Seat Cushions (Right) */}
      <Instances range={sofas.length} geometry={cushionGeo} material={CUSHION_MAT}>
        {sofas.map((s, i) => (
          <Instance 
            key={i} 
            position={[s.position[0] + 0.75 * s.scale, s.position[1] + 0.55 * s.scale, s.position[2]]} 
            rotation={s.rotation} 
            scale={s.scale} 
          />
        ))}
      </Instances>

      {/* 4. Backrest Cushions */}
      <Instances range={sofas.length} geometry={backrestGeo} material={BACKREST_MAT}>
        {sofas.map((s, i) => (
          <group key={i} position={s.position} rotation={s.rotation} scale={s.scale}>
             <Instance position={[-0.75, 1.35, -0.45]} rotation={[-0.1, 0, 0]} />
             <Instance position={[0.75, 1.35, -0.45]} rotation={[-0.1, 0, 0]} />
          </group>
        ))}
      </Instances>

      {/* 5. Armrests */}
      <Instances range={sofas.length} geometry={armrestGeo} material={ARMREST_MAT}>
        {sofas.map((s, i) => (
          <group key={i} position={s.position} rotation={s.rotation} scale={s.scale}>
            <Instance position={[-1.65, 0.5, 0]} />
            <Instance position={[1.65, 0.5, 0]} />
          </group>
        ))}
      </Instances>

      {/* 6. Legs */}
      <Instances range={sofas.length * 4} geometry={legGeo} material={LEG_MAT}>
        {sofas.map((s, i) => (
          <group key={i} position={s.position} rotation={s.rotation} scale={s.scale}>
            {[[-1.4, -0.1, 0.4], [1.4, -0.1, 0.4], [-1.4, -0.1, -0.4], [1.4, -0.1, -0.4]].map((pos, j) => (
              <Instance key={j} position={pos as [number, number, number]} />
            ))}
          </group>
        ))}
      </Instances>

      {/* 7. Ground Glow Shadows */}
      <Instances range={sofas.length} geometry={shadowGeo} material={SHADOW_MAT}>
        {sofas.map((s, i) => (
          <Instance 
            key={i} 
            position={[s.position[0], s.position[1] - 0.2 * s.scale, s.position[2]]} 
            rotation={[-Math.PI/2, 0, 0]} 
            scale={s.scale} 
          />
        ))}
      </Instances>
    </group>
  );
};

export const DirectorsSofa: FC<{ 
  position?: [number, number, number], 
  rotation?: [number, number, number], 
  scale?: number,
  opacity?: number
}> = ({ 
  position = [0, 0, 0], 
  rotation = [0, 0, 0],
  scale = 1,
  opacity = 1
}) => {
  return (
    <group position={position} rotation={rotation} scale={scale}>
      <RoundedBox args={[3.4, 0.2, 1.3]} radius={0.05} smoothness={4} position={[0, 0.1, 0]}>
        <meshStandardMaterial color="#475569" metalness={0.4} roughness={0.6} transparent={opacity < 1} opacity={opacity} />
      </RoundedBox>
      <group position={[0, 0.55, 0]}>
        <RoundedBox args={[1.45, 0.7, 1.1]} radius={0.15} smoothness={8} position={[-0.75, 0, 0]}>
          <meshPhysicalMaterial color="#334155" roughness={0.9} sheen={1} sheenRoughness={0.5} sheenColor="#64748b" emissive="#38bdf8" emissiveIntensity={0.05 * opacity} transparent={opacity < 1} opacity={opacity} />
        </RoundedBox>
        <RoundedBox args={[1.45, 0.7, 1.1]} radius={0.15} smoothness={8} position={[0.75, 0, 0]}>
          <meshPhysicalMaterial color="#334155" roughness={0.9} sheen={1} sheenRoughness={0.5} sheenColor="#64748b" emissive="#38bdf8" emissiveIntensity={0.05 * opacity} transparent={opacity < 1} opacity={opacity} />
        </RoundedBox>
      </group>
      <group position={[0, 1.35, -0.45]} rotation={[-0.1, 0, 0]}>
        <RoundedBox args={[1.45, 1.1, 0.4]} radius={0.2} smoothness={8} position={[-0.75, 0, 0]}>
          <meshPhysicalMaterial color="#334155" roughness={0.9} sheen={1} sheenColor="#64748b" emissive="#38bdf8" emissiveIntensity={0.03 * opacity} transparent={opacity < 1} opacity={opacity} />
        </RoundedBox>
        <RoundedBox args={[1.45, 1.1, 0.4]} radius={0.2} smoothness={8} position={[0.75, 0, 0]}>
          <meshPhysicalMaterial color="#334155" roughness={0.9} sheen={1} sheenColor="#64748b" emissive="#38bdf8" emissiveIntensity={0.03 * opacity} transparent={opacity < 1} opacity={opacity} />
        </RoundedBox>
      </group>
      <RoundedBox args={[0.3, 0.8, 1.25]} radius={0.1} smoothness={8} position={[-1.65, 0.5, 0]}>
        <meshPhysicalMaterial color="#475569" metalness={0.1} roughness={0.8} transparent={opacity < 1} opacity={opacity} />
      </RoundedBox>
      <RoundedBox args={[0.3, 0.8, 1.25]} radius={0.1} smoothness={8} position={[1.65, 0.5, 0]}>
        <meshPhysicalMaterial color="#475569" metalness={0.1} roughness={0.8} transparent={opacity < 1} opacity={opacity} />
      </RoundedBox>
      {[[-1.4, -0.1, 0.4], [1.4, -0.1, 0.4], [-1.4, -0.1, -0.4], [1.4, -0.1, -0.4]].map((pos, i) => (
        <mesh key={i} position={pos as [number, number, number]}>
          <cylinderGeometry args={[0.04, 0.02, 0.4, 16]} />
          <meshStandardMaterial color="#1e293b" metalness={0.7} roughness={0.3} transparent={opacity < 1} opacity={opacity} />
        </mesh>
      ))}
      <mesh position={[0, -0.2, 0]} rotation={[-Math.PI/2, 0, 0]}>
        <planeGeometry args={[3.8, 2.5]} />
        <meshStandardMaterial color="#38bdf8" transparent opacity={0.08 * opacity} depthWrite={false} polygonOffset polygonOffsetFactor={-1} />
      </mesh>
    </group>
  );
};
